type GoogleTag = {
  cmd: Array<() => void>;
  enums: { OutOfPageFormat: { REWARDED: string } };
  defineOutOfPageSlot: (path: string, format: string) => GoogleSlot | null;
  pubads: () => GooglePubAds;
  enableServices: () => void;
  display: (slot: GoogleSlot) => void;
  destroySlots: (slots: GoogleSlot[]) => boolean;
};

type GoogleSlot = {
  addService: (service: GooglePubAds) => GoogleSlot;
};

type GooglePubAds = {
  addEventListener: (name: string, callback: (event: { slot: GoogleSlot; makeRewardedVisible?: () => void }) => void) => void;
};

declare global {
  interface Window {
    googletag?: GoogleTag;
  }
}

let scriptPromise: Promise<void> | null = null;

function loadGooglePublisherTag(): Promise<void> {
  if (window.googletag?.defineOutOfPageSlot) return Promise.resolve();
  if (scriptPromise) return scriptPromise;
  window.googletag = window.googletag ?? { cmd: [] } as unknown as GoogleTag;
  scriptPromise = new Promise((resolve, reject) => {
    const script = document.createElement("script");
    script.async = true;
    script.src = "https://securepubads.g.doubleclick.net/tag/js/gpt.js";
    script.crossOrigin = "anonymous";
    script.onload = () => resolve();
    script.onerror = () => reject(new Error("REWARDED_AD_PROVIDER_FAILED"));
    document.head.appendChild(script);
  });
  return scriptPromise;
}

export async function showRewardedAd(adUnitPath: string | null): Promise<void> {
  if (!ADS_ENABLED) throw new Error("REWARDED_AD_UNAVAILABLE");
  if (!adUnitPath) {
    await new Promise((resolve) => window.setTimeout(resolve, 600));
    return;
  }
  await loadGooglePublisherTag();
  const googletag = window.googletag;
  if (!googletag) throw new Error("REWARDED_AD_PROVIDER_FAILED");

  await new Promise<void>((resolve, reject) => {
    googletag.cmd.push(() => {
      const slot = googletag.defineOutOfPageSlot(adUnitPath, googletag.enums.OutOfPageFormat.REWARDED);
      if (!slot) {
        reject(new Error("REWARDED_AD_UNAVAILABLE"));
        return;
      }
      const pubads = googletag.pubads();
      let granted = false;
      const cleanup = () => googletag.destroySlots([slot]);
      pubads.addEventListener("rewardedSlotReady", (event) => {
        if (event.slot === slot) event.makeRewardedVisible?.();
      });
      pubads.addEventListener("rewardedSlotGranted", (event) => {
        if (event.slot !== slot || granted) return;
        granted = true;
        cleanup();
        resolve();
      });
      pubads.addEventListener("rewardedSlotClosed", (event) => {
        if (event.slot !== slot || granted) return;
        cleanup();
        reject(new Error("REWARDED_AD_NOT_COMPLETED"));
      });
      slot.addService(pubads);
      googletag.enableServices();
      googletag.display(slot);
    });
  });
}
import { ADS_ENABLED } from "./ad-config";
